import { useCallback, useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import {
  fetchRequestsForAgentCollectionScope,
  mapCollectionsForAgentMenus,
  type AgentCollectionOption,
} from './agentCollectionScope';
import { APPLY_ENV_SCOPE_ALL } from './agentApplyEnvUtils';

/** Sentinel for "All Collections" / "All Requests" in collection / request scope dropdowns. */
export const AGENT_SCOPE_ALL = '__all__' as const;

export type AgentScopeAll = typeof AGENT_SCOPE_ALL;

export type AgentScopedListItem = { id: number; name: string; depth?: number; label?: string };

const BODY_REQUEST_METHODS = new Set(['POST', 'PUT', 'PATCH']);

export function isAgentScopeAllLabel(label: string): boolean {
  const normalized = label.trim().toLowerCase();
  return (
    normalized === 'all' ||
    normalized === 'all collections' ||
    normalized === 'all requests' ||
    normalized === 'all collection'
  );
}

/** Shared scoped payload builder (omit All Collections / All Requests). */
export function buildAgentScopedPayload(
  projectId: number,
  collectionId: number | AgentScopeAll,
  requestId: number | AgentScopeAll,
): Record<string, unknown> {
  const data: Record<string, unknown> = { projectId };
  if (collectionId !== AGENT_SCOPE_ALL) {
    data.collectionId = collectionId;
  }
  if (requestId !== AGENT_SCOPE_ALL) {
    data.requestId = requestId;
  }
  return data;
}

export function buildSuggestAssertionsPayload(
  projectId: number,
  collectionId: number | AgentScopeAll,
  requestId: number | AgentScopeAll,
): Record<string, unknown> {
  return buildAgentScopedPayload(projectId, collectionId, requestId);
}

export function buildSuggestAssertionsUserMessage(
  projectLabel: string,
  collectionLabel: string,
  requestLabel: string,
): string {
  const scopeParts: string[] = [];
  if (!isAgentScopeAllLabel(collectionLabel)) {
    scopeParts.push(`collection "${collectionLabel}"`);
  }
  if (!isAgentScopeAllLabel(requestLabel)) {
    scopeParts.push(`request "${requestLabel}"`);
  }
  if (scopeParts.length === 0) {
    return `Generate suggested assertions for "${projectLabel}" (all collections, all requests)`;
  }
  return `Generate suggested assertions for "${projectLabel}" — ${scopeParts.join(', ')}`;
}

export function buildApplyDefaultHeadersUserMessage(
  projectLabel: string,
  collectionLabel: string,
  requestLabel: string,
): string {
  const scopeParts: string[] = [];
  if (!isAgentScopeAllLabel(collectionLabel)) {
    scopeParts.push(`collection "${collectionLabel}"`);
  }
  if (!isAgentScopeAllLabel(requestLabel)) {
    scopeParts.push(`request "${requestLabel}"`);
  }
  if (scopeParts.length === 0) {
    return `Apply default headers for project "${projectLabel}" (all collections, all requests)`;
  }
  return `Apply default headers for project "${projectLabel}" — ${scopeParts.join(', ')}`;
}

export function buildApplyBearerUserMessage(
  projectLabel: string,
  collectionLabel: string,
  requestLabel: string,
): string {
  const scopeParts: string[] = [];
  if (!isAgentScopeAllLabel(collectionLabel)) {
    scopeParts.push(`collection "${collectionLabel}"`);
  }
  if (!isAgentScopeAllLabel(requestLabel)) {
    scopeParts.push(`request "${requestLabel}"`);
  }
  if (scopeParts.length === 0) {
    return `Apply Bearer token to requests in project "${projectLabel}" (all collections, all requests)`;
  }
  return `Apply Bearer token in project "${projectLabel}" — ${scopeParts.join(', ')}`;
}

export type UseAgentSuggestAssertionsScopeOptions = {
  /** When true, only POST/PUT/PATCH requests are listed (default headers / body scope). */
  bodyMethodsOnly?: boolean;
  initialCollectionId?: number | AgentScopeAll;
  initialRequestId?: number | AgentScopeAll;
};

export function useAgentSuggestAssertionsScope(
  projectId: number | '',
  options?: UseAgentSuggestAssertionsScopeOptions,
) {
  const bodyMethodsOnly = Boolean(options?.bodyMethodsOnly);
  const initialCollectionId = options?.initialCollectionId;
  const initialRequestId = options?.initialRequestId;
  const [collectionId, setCollectionId] = useState<number | AgentScopeAll>(
    initialCollectionId ?? AGENT_SCOPE_ALL,
  );
  const [requestId, setRequestId] = useState<number | AgentScopeAll>(
    initialRequestId ?? AGENT_SCOPE_ALL,
  );
  const [collections, setCollections] = useState<AgentScopedListItem[]>([]);
  const [collectionTree, setCollectionTree] = useState<AgentCollectionOption[]>([]);
  const [requests, setRequests] = useState<AgentScopedListItem[]>([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);

  const resetScope = useCallback(() => {
    setCollectionId(AGENT_SCOPE_ALL);
    setRequestId(AGENT_SCOPE_ALL);
    setCollections([]);
    setCollectionTree([]);
    setRequests([]);
  }, []);

  useEffect(() => {
    if (initialCollectionId != null) {
      setCollectionId(initialCollectionId);
    } else {
      setCollectionId(AGENT_SCOPE_ALL);
    }
    // Request selection is applied after requests load (see fetch effect below).
    setRequestId(AGENT_SCOPE_ALL);
    if (projectId === '') {
      setCollections([]);
      setCollectionTree([]);
      setRequests([]);
      return;
    }
    let cancelled = false;
    setLoadingCollections(true);
    void apiService
      .getCollectionsByProject(Number(projectId))
      .then((rows) => {
        if (cancelled) return;
        const mapped = mapCollectionsForAgentMenus(rows);
        setCollectionTree(mapped);
        setCollections(
          mapped.map((c) => ({
            id: c.id,
            name: c.name,
            depth: c.depth,
            label: c.label,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setCollections([]);
          setCollectionTree([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCollections(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, initialCollectionId]);

  useEffect(() => {
    if (projectId === '' || loadingCollections) {
      if (projectId === '') setRequests([]);
      return;
    }
    let cancelled = false;
    setLoadingRequests(true);
    const scope =
      collectionId === AGENT_SCOPE_ALL ? APPLY_ENV_SCOPE_ALL : collectionId;
    void fetchRequestsForAgentCollectionScope(
      Number(projectId),
      scope,
      collectionTree,
      bodyMethodsOnly ? { methods: BODY_REQUEST_METHODS } : undefined,
    )
      .then((rows) => {
        if (cancelled) return;
        const mapped = rows.map((r) => ({ id: r.id, name: r.name }));
        setRequests(mapped);
        setRequestId((prev) => {
          if (prev !== AGENT_SCOPE_ALL && mapped.some((r) => r.id === prev)) {
            return prev;
          }
          const collectionMatchesInitial =
            initialCollectionId == null ||
            initialCollectionId === AGENT_SCOPE_ALL ||
            initialCollectionId === collectionId;
          if (
            initialRequestId != null &&
            initialRequestId !== AGENT_SCOPE_ALL &&
            collectionMatchesInitial &&
            mapped.some((r) => r.id === initialRequestId)
          ) {
            return initialRequestId;
          }
          return AGENT_SCOPE_ALL;
        });
      })
      .catch(() => {
        if (!cancelled) {
          setRequests([]);
          setRequestId(AGENT_SCOPE_ALL);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRequests(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    projectId,
    collectionId,
    bodyMethodsOnly,
    collectionTree,
    loadingCollections,
    initialRequestId,
    initialCollectionId,
  ]);

  const handleCollectionChange = useCallback((next: number | AgentScopeAll) => {
    setCollectionId(next);
    setRequestId(AGENT_SCOPE_ALL);
  }, []);

  return {
    collectionId,
    setCollectionId: handleCollectionChange,
    requestId,
    setRequestId,
    collections,
    requests,
    loadingCollections,
    loadingRequests,
    resetScope,
  };
}
